import React from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Alert,
  Switch,
} from "react-native";
import { platformAlert, showAlert } from "../utils/platformAlert";
import { Image } from "expo-image";
import { useRoute, RouteProp, useNavigation } from "@react-navigation/native";
import { NativeStackNavigationProp } from "@react-navigation/native-stack";
import { Feather } from "@expo/vector-icons";
import { format } from "date-fns";
import Constants from "expo-constants";

import { spacing, typography } from "../theme";
import { useColors, ThemeColors } from "../hooks/useColors";
import { supabase } from "../lib/supabase";
import { useUberDelivery } from "../hooks/use-uber-delivery"; // optional
import { useVendorProfile } from "../hooks/useVendorProfile";
import { useNotifications } from "../hooks/useNotifications";
import GeocodingService from "../services/GeocodingService";
import { uberDirectService } from "../services/UberDirectService";
import type { RootStackParamList } from "../navigation/types";
import { DELIVERY_CONFIG, DeliveryHelpers } from "../config/deliveryConfig";
import { formatPrice } from "../utils/format";
import { findOrCreateChat } from "../utils/chatUtils";
import { useAuth } from "../hooks/useAuth";
import { logger } from "../utils/logger";
import { orderEvents } from "../utils/orderEvents";

type Nav = NativeStackNavigationProp<RootStackParamList>;
type RouteP = RouteProp<{ params: { vendorOrderId: string; autoStartDelivery?: boolean } }, "params">;

type VendorOrder = {
  id: string;
  vendor_id: string;
  buyer_id: string;
  status:
    | "pending"
    | "confirmed"
    | "paid"
    | "wrapping_up"
    | "packed"
    | "pickup_pending"
    | "in_transit"
    | "completed"
    | "rejected"
    | "cancelled";
  items: Array<{
    name: string;
    quantity: number;
    unit_price?: number; // dollars or cents (adjust below)
    product_id?: string;
    image?: string | null;
  }>;
  sub_total?: number;
  delivery_fee?: number | null;
  total_amount: number;
  delivery_id?: string | null;
  tracking_url?: string | null;
  created_at: string;
  parent_order_id?: string | null;

  // Delivery-related fields for vendor-initiated delivery
  pickup_address?: any;
  dropoff_address?: any;
  delivery_quote?: {
    quote_id: string;
    pickup_latitude: number;
    pickup_longitude: number;
    dropoff_latitude: number;
    dropoff_longitude: number;
    pickup_phone_number?: string;
    dropoff_phone_number?: string;
    fee: number;
  };
  pickup_contact?: {
    name: string;
    phone: string;
  };
  dropoff_contact?: {
    name: string;
    phone: string;
  };
};

export default function VendorOrderDetailsScreen() {
  const colors = useColors();
  const styles = createStyles(colors);
  const navigation = useNavigation<Nav>();
  const route = useRoute<RouteP>();
  const { vendorOrderId, autoStartDelivery } = route.params;

  // const uberDelivery = useUberDelivery(); // optional - not needed since we use edge function
  const { data: vendorProfile } = useVendorProfile();
  const { sendOrderUpdate } = useNotifications();
  const { user } = useAuth();
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [order, setOrder] = React.useState<VendorOrder | null>(null);
  const [isTestMode, setIsTestMode] = React.useState(false);
  const [startingChat, setStartingChat] = React.useState(false);

  // Use Constants for runtime access or fallback to process.env for build-time
  const ENABLE_CANCEL_DELIVERY =
    Constants?.expoConfig?.extra?.EXPO_PUBLIC_ENABLE_CANCEL_DELIVERY === "true" ||
    process.env.EXPO_PUBLIC_ENABLE_CANCEL_DELIVERY === "true" ||
    true; // Default to true since we enabled it in eas.json

  console.log("VendorOrderDetailsScreen order:", order);

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // Load the vendor order
        const { data, error } = await supabase
          .from("vendor_orders")
          .select("*")
          .eq("id", vendorOrderId)
          .maybeSingle();

        if (error) throw error;
        if (!data) throw new Error("Order not found");

        // Optionally hydrate item images if missing (first image from products)
        const items = await Promise.all(
          (data.items || []).map(async (it: any) => {
            if (it?.image || !it?.product_id) return it;
            const { data: p } = await supabase
              .from("products")
              .select("images")
              .eq("id", it.product_id)
              .maybeSingle();
            const img =
              (Array.isArray(p?.images) && p?.images?.[0]) ||
              (typeof p?.images === "string" ? p?.images : null);
            return { ...it, image: img || null };
          })
        );

        setOrder({ ...data, items });
      } catch (e: any) {
        Alert.alert("Error", e?.message || "Failed to load order");
      } finally {
        setLoading(false);
      }
    })();
  }, [vendorOrderId]);

  // Auto-start delivery when navigating from "Mark as Packed" action
  // Only triggers ONCE - the param is cleared after first attempt to prevent retries on refresh
  const autoStartTriggeredRef = React.useRef(false);
  React.useEffect(() => {
    if (
      autoStartDelivery &&
      !autoStartTriggeredRef.current &&
      order &&
      order.status === "packed" &&
      !order.delivery_id && // Don't auto-start if delivery already exists
      !saving &&
      !loading &&
      order.delivery_quote?.quote_id
    ) {
      autoStartTriggeredRef.current = true;
      console.log("🚚 [AUTO-DELIVERY] Auto-starting delivery for packed order");
      
      // Clear the autoStartDelivery param to prevent retries on refresh
      navigation.setParams({ autoStartDelivery: undefined } as any);
      
      // Small delay to ensure UI is rendered
      setTimeout(() => {
        requestDelivery();
      }, 500);
    }
  }, [autoStartDelivery, order, saving, loading, navigation]);

  /**
   * Handle delivery provider errors by refunding the buyer and sending notification
   */
  const handleDeliveryProviderError = async (orderData: VendorOrder, errorDetails: string) => {
    console.error("🚨 [DELIVERY-ERROR] Handling delivery provider error:", errorDetails);
    
    let refundSucceeded = false;
    let notificationSent = false;
    
    try {
      // 1. Get the parent order to retrieve payment_intent_id
      let paymentIntentId: string | null = null;
      if (orderData.parent_order_id) {
        const { data: parentOrder, error: parentError } = await supabase
          .from("orders")
          .select("payment_intent_id")
          .eq("id", orderData.parent_order_id)
          .single();
        
        if (parentError) {
          console.error("❌ [DELIVERY-ERROR] Failed to fetch parent order:", parentError);
        } else {
          paymentIntentId = parentOrder?.payment_intent_id;
        }
      }

      // 2. Process refund if we have a payment_intent_id
      if (paymentIntentId) {
        console.log("💰 [DELIVERY-ERROR] Processing refund for payment:", paymentIntentId);
        try {
          const { data: refundResult, error: refundError } = await supabase.functions.invoke(
            "refund-payment",
            {
              body: {
                payment_intent_id: paymentIntentId,
                reason: "requested_by_customer",
                metadata: {
                  order_id: orderData.parent_order_id,
                  vendor_order_id: orderData.id,
                  refund_reason: "delivery_provider_error",
                  error_details: errorDetails,
                },
              },
            }
          );

          // Log full response for debugging
          console.log("📋 [DELIVERY-ERROR] Full refund response:", JSON.stringify(refundResult, null, 2));
          
          // Check for error in response body (edge function returns 200 with error in body)
          if (refundError) {
            console.error("❌ [DELIVERY-ERROR] Refund edge function error:", refundError);
          } else if (refundResult?.error) {
            console.error("❌ [DELIVERY-ERROR] Refund failed:", refundResult.error);
            console.error("❌ [DELIVERY-ERROR] HTTP status from edge function:", refundResult._http_status);
          } else {
            console.log("✅ [DELIVERY-ERROR] Refund processed:", refundResult);
            refundSucceeded = true;
          }
        } catch (refundException) {
          console.error("❌ [DELIVERY-ERROR] Refund exception:", refundException);
        }
      } else {
        console.warn("⚠️ [DELIVERY-ERROR] No payment_intent_id found, cannot process refund");
      }

      // 3. Update order status to cancelled (do this regardless of refund success)
      const { error: updateError } = await supabase
        .from("vendor_orders")
        .update({
          status: "cancelled",
          updated_at: new Date().toISOString(),
        })
        .eq("id", orderData.id);

      if (updateError) {
        console.error("❌ [DELIVERY-ERROR] Failed to update vendor order status:", updateError);
      }

      // Also update parent order if exists
      if (orderData.parent_order_id) {
        await supabase
          .from("orders")
          .update({
            status: "cancelled",
            updated_at: new Date().toISOString(),
          })
          .eq("id", orderData.parent_order_id);
      }

      // 4. ALWAYS send notification to buyer (even if refund failed)
      if (orderData.buyer_id) {
        try {
          const notificationMessage = refundSucceeded 
            ? "An error occurred with our delivery provider. You have been refunded. Please try your order again later."
            : "An error occurred with our delivery provider. Your refund is being processed. Please try your order again later.";
          
          const { data: notifResult, error: notifError } = await supabase.functions.invoke("send-notification", {
            body: {
              user_id: orderData.buyer_id,
              title: "Order Cancelled",
              body: notificationMessage,
              notification_type: "order_status",
              data: {
                order_id: orderData.parent_order_id,
                vendor_order_id: orderData.id,
                status: "cancelled",
              },
            },
          });
          
          // Check for error in response body
          if (notifError) {
            console.error("❌ [DELIVERY-ERROR] Notification edge function error:", notifError);
          } else if (notifResult?.error) {
            console.error("❌ [DELIVERY-ERROR] Notification failed:", notifResult.error);
          } else {
            console.log("✅ [DELIVERY-ERROR] Notification sent to buyer");
            notificationSent = true;
          }

          // Send SMS to buyer if they don't have native app (web-only users)
          const buyerPhone = orderData.dropoff_contact?.phone || orderData.delivery_quote?.dropoff_phone_number;
          if (buyerPhone) {
            try {
              const smsMessage = refundSucceeded
                ? "Your JadeTrade order was cancelled due to a delivery error. You have been refunded."
                : "Your JadeTrade order was cancelled due to a delivery error. Refund is being processed.";
              
              const { data: smsResult } = await supabase.functions.invoke("send-sms", {
                body: {
                  to: buyerPhone,
                  message: smsMessage,
                  user_id: orderData.buyer_id,
                },
              });

              if (smsResult?.skipped) {
                console.log("📱 [DELIVERY-ERROR] SMS skipped:", smsResult.skip_reason);
              } else if (smsResult?.success) {
                console.log("📱 [DELIVERY-ERROR] SMS sent to buyer");
              } else {
                console.warn("⚠️ [DELIVERY-ERROR] SMS failed:", smsResult?.error);
              }
            } catch (smsError) {
              console.warn("⚠️ [DELIVERY-ERROR] Failed to send SMS:", smsError);
            }
          }
        } catch (notifException) {
          console.error("⚠️ [DELIVERY-ERROR] Failed to send notification:", notifException);
        }
      }

      // 5. Update local state
      setOrder((o) => (o ? { ...o, status: "cancelled" } : o));

      // 6. Show alert to vendor
      const vendorMessage = refundSucceeded
        ? "An error occurred with our delivery provider. The buyer has been notified and refunded."
        : "An error occurred with our delivery provider. The buyer has been notified. Please contact support to process the refund manually.";
      
      Alert.alert(
        "Delivery Error",
        vendorMessage,
        [{ text: "OK", onPress: () => {
          if (navigation.canGoBack()) {
            navigation.goBack();
          } else {
            // Navigate to Home tab if there's no screen to go back to
            (navigation as any).navigate("Main");
          }
        }}]
      );
    } catch (error) {
      console.error("❌ [DELIVERY-ERROR] Error handling delivery failure:", error);
      Alert.alert(
        "Error",
        "Failed to process delivery error. Please contact support.",
        [{ text: "OK" }]
      );
    }
  };

  const cancelOrder = async () => {
    if (!order) return;

    // Check if this is a paid order that needs refund
    const needsRefund = ["paid", "wrapping_up", "packed"].includes(order.status);
    const confirmMessage = needsRefund
      ? "Are you sure? This will cancel the order, refund the buyer, and cannot be undone."
      : "Are you sure? This cannot be undone.";

    platformAlert("Cancel order", confirmMessage, [
      { text: "Keep", style: "cancel" },
      {
        text: "Cancel",
        style: "destructive",
        onPress: async () => {
          try {
            setSaving(true);
            let refundSucceeded = false;

            // Restore inventory for all items in the order
            console.log("[VendorOrderDetails] Cancel - Order items:", JSON.stringify(order.items, null, 2));
            if (order.items && order.items.length > 0) {
              console.log("[VendorOrderDetails] Restoring inventory for cancelled order:", order.id, "Items count:", order.items.length);
              
              for (const item of order.items) {
                console.log("[VendorOrderDetails] Cancel - Processing item:", { product_id: item.product_id, quantity: item.quantity, name: item.name });
                if (item.product_id && item.quantity > 0) {
                  // Get current product quantity
                  const { data: product, error: fetchErr } = await supabase
                    .from("products")
                    .select("quantity, status")
                    .eq("id", item.product_id)
                    .single();
                  
                  if (fetchErr) {
                    console.error(`[VendorOrderDetails] Failed to fetch product ${item.product_id}:`, fetchErr);
                    continue;
                  }
                  
                  const currentQuantity = Number(product?.quantity ?? 0);
                  const newQuantity = currentQuantity + item.quantity;
                  
                  // Update the product quantity and set status back to 'active' if it was 'sold'
                  const updateData: { quantity: number; status?: string } = { quantity: newQuantity };
                  if (product?.status === "sold") {
                    updateData.status = "active";
                  }
                  
                  const { error: updateErr } = await supabase
                    .from("products")
                    .update(updateData)
                    .eq("id", item.product_id);
                  
                  if (updateErr) {
                    console.error(`[VendorOrderDetails] Failed to restore quantity for product ${item.product_id}:`, updateErr);
                  } else {
                    console.log(`[VendorOrderDetails] Restored ${item.quantity} units to product ${item.product_id} (new quantity: ${newQuantity})`);
                  }
                }
              }
            }

            // Process refund for paid orders
            if (needsRefund && order.parent_order_id) {
              console.log("[VendorOrderDetails] Processing refund for cancelled order");
              
              // Get payment_intent_id from parent order
              const { data: parentOrder, error: parentError } = await supabase
                .from("orders")
                .select("payment_intent_id")
                .eq("id", order.parent_order_id)
                .single();
              
              if (parentError) {
                console.error("[VendorOrderDetails] Failed to fetch parent order:", parentError);
              } else if (parentOrder?.payment_intent_id) {
                try {
                  const { data: refundResult, error: refundError } = await supabase.functions.invoke(
                    "refund-payment",
                    {
                      body: {
                        payment_intent_id: parentOrder.payment_intent_id,
                        reason: "requested_by_customer",
                        metadata: {
                          order_id: order.parent_order_id,
                          vendor_order_id: order.id,
                          refund_reason: "order_cancelled_by_vendor",
                        },
                      },
                    }
                  );

                  if (refundError) {
                    console.error("[VendorOrderDetails] Refund error:", refundError);
                  } else if (refundResult?.error) {
                    console.error("[VendorOrderDetails] Refund failed:", refundResult.error);
                  } else if (refundResult?.success) {
                    console.log("[VendorOrderDetails] Refund processed successfully:", refundResult);
                    refundSucceeded = true;
                  }
                } catch (refundException) {
                  console.error("[VendorOrderDetails] Refund exception:", refundException);
                }
              } else {
                console.warn("[VendorOrderDetails] No payment_intent_id found for refund");
              }
            }

            // Update the vendor order to cancelled
            // IMPORTANT: Update orders table FIRST (before vendor_orders)
            // This is required because RLS policy on orders checks vendor_orders via EXISTS.
            if (order.parent_order_id) {
              const { error: orderError } = await supabase
                .from("orders")
                .update({
                  status: "cancelled",
                  updated_at: new Date().toISOString(),
                })
                .eq("id", order.parent_order_id);
              if (orderError) {
                console.warn("Failed to update orders table:", orderError);
                // Continue with vendor_orders update
              }
            }

            // Now update vendor_orders table
            const { error: upErr } = await supabase
              .from("vendor_orders")
              .update({
                status: "cancelled",
                updated_at: new Date().toISOString(),
              })
              .eq("id", order.id);

            if (upErr) throw upErr;

            // Send notification to buyer
            if (order.buyer_id) {
              try {
                const notificationMessage = needsRefund
                  ? (refundSucceeded
                      ? "Your order has been cancelled by the vendor. You have been refunded and should see it in your account within 5-10 business days."
                      : "Your order has been cancelled by the vendor. Your refund is being processed.")
                  : "Your order has been cancelled by the vendor.";
                
                await supabase.functions.invoke("send-notification", {
                  body: {
                    user_id: order.buyer_id,
                    title: "Order Cancelled",
                    body: notificationMessage,
                    notification_type: "order_status",
                    data: {
                      order_id: order.parent_order_id,
                      vendor_order_id: order.id,
                      status: "cancelled",
                    },
                  },
                });
                console.log("[VendorOrderDetails] Cancellation notification sent to buyer");
              } catch (notifError) {
                console.warn("[VendorOrderDetails] Failed to send notification:", notifError);
              }
            }

            setOrder((o) => (o ? { ...o, status: "cancelled" } : o));
            
            // Emit order event to notify other components (like OngoingVendorOrderDisplay and OngoingOrderDisplay)
            orderEvents.emit();
            
            // Show appropriate message
            const successMessage = needsRefund
              ? (refundSucceeded
                  ? "The order has been cancelled and the buyer has been refunded."
                  : "The order has been cancelled. The buyer's refund is being processed.")
              : "The order has been cancelled.";
            showAlert("Cancelled", successMessage);
          } catch (e: any) {
            showAlert("Error", e?.message || "Failed to cancel the order");
          } finally {
            setSaving(false);
          }
        },
      },
    ]);
  };

  const cancelDelivery = async () => {
    if (!order || !order.delivery_id) return;

    platformAlert(
      "Cancel Delivery",
      "Are you sure you want to cancel the delivery? This will stop the courier, cancel the order, and refund the buyer.",
      [
        { text: "Keep Delivery", style: "cancel" },
        {
          text: "Cancel Delivery",
          style: "destructive",
          onPress: async () => {
            try {
              setSaving(true);

              // Call the uber-cancel-delivery function which handles:
              // 1. Cancelling with Uber
              // 2. Updating order status
              // 3. Restoring inventory
              // 4. Processing refund
              // 5. Sending notification to buyer
              const { data, error } = await supabase.functions.invoke(
                "uber-cancel-delivery",
                {
                  body: {
                    delivery_id: order.delivery_id,
                    reason: "cancelled_by_vendor",
                    vendor_order_id: order.id,
                    parent_order_id: order.parent_order_id
                  }
                }
              );

              if (error) throw error;

              console.log("[VendorOrderDetails] Delivery cancelled successfully:", data);

              // Update local state to reflect the cancellation
              setOrder((o) => (o ? { ...o, status: "cancelled" } : o));
              
              // Emit order event to notify other components (like OngoingVendorOrderDisplay and OngoingOrderDisplay)
              orderEvents.emit();
              
              // Show appropriate message based on refund status
              const refundMessage = data?.refund?.processed 
                ? "The delivery has been cancelled and the buyer has been refunded."
                : "The delivery has been cancelled. The buyer's refund is being processed.";
              showAlert("Delivery Cancelled", refundMessage);
            } catch (e: any) {
              showAlert("Error", e?.message || "Failed to cancel the delivery");
            } finally {
              setSaving(false);
            }
          },
        },
      ]
    );
  };

  const startChatWithBuyer = async () => {
    if (!user) {
      platformAlert("Error", "Please sign in to message the buyer.");
      return;
    }

    if (!order?.buyer_id) {
      platformAlert("Error", "Unable to start chat - buyer information is missing.");
      return;
    }

    setStartingChat(true);

    try {
      logger.debug('Chat', `Starting chat between vendor user ${user.id} and buyer ${order.buyer_id}`);

      // Find or create chat - vendor_id is optional here since we're initiating from vendor side
      const chatId = await findOrCreateChat(user.id, order.buyer_id, vendorProfile?.id || null);
      
      logger.debug('Chat', `Navigating to chat: ${chatId}`);
      (navigation as any).navigate("Chat", { id: chatId });
      
    } catch (error) {
      logger.error('Chat', `Error starting chat:`, error);
      platformAlert("Error", "Unable to start chat. Please try again.");
    } finally {
      setStartingChat(false);
    }
  };

  // Status helpers
  const statusMeta = React.useMemo(() => {
    const s = order?.status;
    const jadeGreen = "#00A86B";
    const roseRed = "#F43F5E";
    const map: Record<string, { label: string; bgColor: string; icon: any }> = {
      pending: {
        label: "Pending Approval",
        bgColor: jadeGreen,
        icon: "clock",
      },
      confirmed: { label: "Approved", bgColor: jadeGreen, icon: "check-circle" },
      paid: {
        label: "Paid · Action Required",
        bgColor: jadeGreen,
        icon: "alert-circle",
      },
      wrapping_up: {
        label: "Wrapping Up",
        bgColor: jadeGreen,
        icon: "package",
      },
      packed: { label: "Packed", bgColor: jadeGreen, icon: "package" },
      pickup_pending: {
        label: "Courier On The Way",
        bgColor: jadeGreen,
        icon: "truck",
      },
      in_transit: {
        label: "In Transit",
        bgColor: jadeGreen,
        icon: "truck",
      },
      completed: { label: "Completed", bgColor: jadeGreen, icon: "check-circle" },
      delivered: { label: "Delivered", bgColor: jadeGreen, icon: "check-circle" },
      pickup_complete: { label: "Pickup Complete", bgColor: jadeGreen, icon: "check-circle" },
      rejected: { label: "Rejected", bgColor: roseRed, icon: "x-circle" },
      cancelled: { label: "Cancelled", bgColor: roseRed, icon: "x-circle" },
    };
    const defaultMeta = { label: "—", bgColor: jadeGreen, icon: "help-circle" };
    return s ? (map[s] || defaultMeta) : defaultMeta;
  }, [order?.status]);

  // Button visibility based on corrected status requirements
  const canApprove = order?.status === "pending"; // Confirm order button
  const canReject = order?.status === "pending"; // Reject order button
  const canSetWrappingUp = order?.status === "paid"; // Set "Wrapping up" button
  const canPack = order?.status === "wrapping_up"; // Mark as packed button
  const canRequestDelivery = order?.status === "packed"; // Start Delivery button

  // Cancel button logic: show for statuses BEFORE vendor marks as packed
  // Once packed, delivery is automatically started and cancellation would incur fees
  const canCancel = ["pending", "confirmed", "paid", "wrapping_up"].includes(order?.status || "");
  
  // Cancel delivery button logic: DISABLED after packed status
  // Once delivery is dispatched (pickup_pending and beyond), cancellation is not allowed
  // to avoid disrupting the courier and incurring cancellation fees
  const canCancelDelivery = false;

  const updateStatus = async (next: VendorOrder["status"]) => {
    if (!order) return;
    setSaving(true);
    try {
      // IMPORTANT: Update orders table FIRST (before vendor_orders)
      // This is required because RLS policy on orders checks vendor_orders via EXISTS.
      // If we update vendor_orders first, the RLS check may fail.
      if (order.parent_order_id) {
        const { error: orderError } = await supabase
          .from("orders")
          .update({ status: next, updated_at: new Date().toISOString() })
          .eq("id", order.parent_order_id);
        if (orderError) {
          console.warn("Failed to update orders table:", orderError);
          // Continue with vendor_orders update
        }
      }

      // Now update vendor_orders table
      const { error: vendorError } = await supabase
        .from("vendor_orders")
        .update({ status: next, updated_at: new Date().toISOString() })
        .eq("id", order.id);
      if (vendorError) throw vendorError;

      setOrder((o) => (o ? { ...o, status: next } : o));
      
      // Emit order event to notify other components (like OngoingVendorOrderDisplay)
      orderEvents.emit();
      
      // Send order status notification to buyer
      if (order.buyer_id && order.parent_order_id) {
        try {
          await sendOrderUpdate(
            order.buyer_id,
            order.parent_order_id,
            next
          );
          console.log('✅ [NOTIFICATION] Order status update sent to buyer');
        } catch (notifError) {
          console.warn('⚠️ [NOTIFICATION] Failed to send status update:', notifError);
          // Don't fail the whole process if notification fails
        }
      }
    } catch (e: any) {
      Alert.alert("Error", e?.message || "Failed to update status");
    } finally {
      setSaving(false);
    }
  };

  const approve = async () => {
    if (!order) return;
    Alert.alert("Approve order", "Confirm you want to approve this order.", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Approve",
        onPress: async () => updateStatus("confirmed"),
      },
    ]);
  };

  const reject = async () => {
    if (!order) return;
    Alert.alert("Reject order", "Are you sure you want to reject this order?", [
      { text: "Cancel", style: "cancel" },
      {
        text: "Reject",
        style: "destructive",
        onPress: async () => {
          setSaving(true);
          try {
            // Restore inventory for all items in the order
            console.log("[VendorOrderDetails] Order items:", JSON.stringify(order.items, null, 2));
            if (order.items && order.items.length > 0) {
              console.log("[VendorOrderDetails] Restoring inventory for rejected order:", order.id, "Items count:", order.items.length);
              
              for (const item of order.items) {
                console.log("[VendorOrderDetails] Processing item:", { product_id: item.product_id, quantity: item.quantity, name: item.name });
                if (item.product_id && item.quantity > 0) {
                  // Get current product quantity
                  const { data: product, error: fetchErr } = await supabase
                    .from("products")
                    .select("quantity, status")
                    .eq("id", item.product_id)
                    .single();
                  
                  if (fetchErr) {
                    console.error(`[VendorOrderDetails] Failed to fetch product ${item.product_id}:`, fetchErr);
                    continue;
                  }
                  
                  const currentQuantity = Number(product?.quantity ?? 0);
                  const newQuantity = currentQuantity + item.quantity;
                  
                  // Update the product quantity and set status back to 'active' if it was 'sold'
                  const updateData: { quantity: number; status?: string } = { quantity: newQuantity };
                  if (product?.status === "sold") {
                    updateData.status = "active";
                  }
                  
                  const { error: updateErr } = await supabase
                    .from("products")
                    .update(updateData)
                    .eq("id", item.product_id);
                  
                  if (updateErr) {
                    console.error(`[VendorOrderDetails] Failed to restore quantity for product ${item.product_id}:`, updateErr);
                  } else {
                    console.log(`[VendorOrderDetails] Restored ${item.quantity} units to product ${item.product_id} (new quantity: ${newQuantity})`);
                  }
                }
              }
            }
            
            // Update the order status to rejected
            await updateStatus("rejected");
          } catch (e: any) {
            console.error("[VendorOrderDetails] Error rejecting order:", e);
            Alert.alert("Error", e?.message || "Failed to reject the order");
          } finally {
            setSaving(false);
          }
        },
      },
    ]);
  };

  const setWrappingUp = async () => updateStatus("wrapping_up");
  
  const markPacked = async () => {
    // First update status to packed
    await updateStatus("packed");
    
    // Then automatically start delivery (same flow as OngoingVendorOrderDisplay)
    // Small delay to ensure UI is rendered, pass skipStatusCheck=true since we just set it
    setTimeout(() => {
      console.log("🚚 [VendorOrderDetails] Auto-starting delivery after marking as packed");
      requestDelivery(true); // Skip status check since we just marked as packed
    }, 500);
  };

  // Phone number sanitizer for E.164 format (Uber Direct requirement)
  const toE164US = (raw: string): string => {
    let s = raw.trim().replace(/[^\d+]/g, "");

    // Already valid E.164
    if (/^\+\d{10,15}$/.test(s)) return s;

    // Remove leading +
    if (s.startsWith("+")) s = s.slice(1);

    // Case: plain 10 digits → assume U.S., add +1
    if (/^\d{10}$/.test(s)) return `+1${s}`;

    // Case: 11 digits starting with 1 → normalize to +1
    if (/^1\d{10}$/.test(s)) return `+${s}`;

    // Fallback → just add +
    return `+${s}`;
  };

  // Enhanced delivery creation using stored quote data
  // skipStatusCheck: true when called immediately after markPacked (state may not have updated yet)
  const requestDelivery = async (skipStatusCheck = false) => {
    if (!order) return;

    // Validate order status (skip if we just marked as packed)
    if (!skipStatusCheck && order.status !== "packed") {
      Alert.alert(
        "Invalid Status",
        "Order must be packed before starting delivery."
      );
      return;
    }

    // Validate required data
    if (!order.items?.length) {
      Alert.alert("Missing items", "This order has no items to ship.");
      return;
    }

    if (!order.delivery_quote?.quote_id) {
      Alert.alert("Missing Quote", "No delivery quote found for this order.");
      return;
    }

    // We'll validate pickup coordinates later with fallback to geocoding

    if (!order.dropoff_address) {
      Alert.alert(
        "Missing Delivery Address",
        "Customer delivery address is required."
      );
      return;
    }

    try {
      setSaving(true);

      // Get current user session for authentication
      const { data: session, error: sessionError } =
        await supabase.auth.getSession();
      if (sessionError || !session.session) {
        throw new Error("Authentication required");
      }

      // Validate vendor profile is available for pickup address
      if (!vendorProfile) {
        Alert.alert(
          "Vendor Profile Required",
          "Unable to create delivery without vendor profile information."
        );
        return;
      }

      // Prepare manifest items
      const manifestItems = order.items.map((item) =>
        DeliveryHelpers.createManifestItem(
          item.name,
          item.quantity
        )
      );

      // Validate vendor profile has all required address fields
      if (!vendorProfile.street_line1 && !vendorProfile.address) {
        Alert.alert(
          "Missing Vendor Address",
          "Vendor street address is required for pickup location."
        );
        return;
      }

      if (!vendorProfile.city) {
        Alert.alert(
          "Missing Vendor City",
          "Vendor city is required for pickup location."
        );
        return;
      }

      if (!vendorProfile.state) {
        Alert.alert(
          "Missing Vendor State",
          "Vendor state is required for pickup location."
        );
        return;
      }

      if (!vendorProfile.zip_code && !vendorProfile.zip) {
        Alert.alert(
          "Missing Vendor Zip Code",
          "Vendor zip code is required for pickup location."
        );
        return;
      }

      // Construct pickup address from vendor profile - no defaults
      const pickupAddress = order.pickup_address || {
        street_address: vendorProfile.street_line1 || vendorProfile.address,
        city: vendorProfile.city,
        state: vendorProfile.state,
        zip_code: vendorProfile.zip_code || vendorProfile.zip,
        country: vendorProfile.country || "US", // US is reasonable default for country
      };

      // Get pickup coordinates with fallback to geocoding
      let pickupLatitude = order.delivery_quote.pickup_latitude;
      let pickupLongitude = order.delivery_quote.pickup_longitude;

      // If coordinates are missing, try to get them from geocoding
      if (!pickupLatitude || !pickupLongitude) {
        console.log(
          "🗺️ [GEOCODING] Pickup coordinates missing, attempting to geocode vendor address"
        );

        const vendorAddressString = GeocodingService.buildAddressString({
          street_address:
            vendorProfile.street_line1 || vendorProfile.address || undefined,
          city: vendorProfile.city || undefined,
          state: vendorProfile.state || undefined,
          zip_code: vendorProfile.zip_code || vendorProfile.zip || undefined,
          country: vendorProfile.country || "US",
        });

        const geocodeResult = await GeocodingService.geocodeAddress(
          vendorAddressString
        );

        if (geocodeResult) {
          pickupLatitude = geocodeResult.latitude;
          pickupLongitude = geocodeResult.longitude;
          console.log(
            "✅ [GEOCODING] Successfully geocoded pickup coordinates:",
            {
              latitude: pickupLatitude,
              longitude: pickupLongitude,
            }
          );
        } else {
          Alert.alert(
            "Unable to Locate Pickup Address",
            "Could not determine pickup location coordinates. Please ensure your vendor address is complete and valid."
          );
          return;
        }
      }

      // Use the dropoff address from the order
      const dropoffAddress = order.dropoff_address;

      // Get dropoff coordinates with fallback to geocoding
      let dropoffLatitude = order.delivery_quote.dropoff_latitude;
      let dropoffLongitude = order.delivery_quote.dropoff_longitude;

      // If dropoff coordinates are missing, try to get them from geocoding
      if (!dropoffLatitude || !dropoffLongitude) {
        console.log(
          "🗺️ [GEOCODING] Dropoff coordinates missing, attempting to geocode customer address"
        );

        const customerAddressString = GeocodingService.buildAddressString({
          street_address: Array.isArray(dropoffAddress.street_address)
            ? dropoffAddress.street_address[0]
            : dropoffAddress.street_address,
          city: dropoffAddress.city,
          state: dropoffAddress.state,
          zip_code: dropoffAddress.zip_code,
          country: dropoffAddress.country,
        });

        const geocodeResult = await GeocodingService.geocodeAddress(
          customerAddressString
        );

        if (geocodeResult) {
          dropoffLatitude = geocodeResult.latitude;
          dropoffLongitude = geocodeResult.longitude;
          console.log(
            "✅ [GEOCODING] Successfully geocoded dropoff coordinates:",
            {
              latitude: dropoffLatitude,
              longitude: dropoffLongitude,
            }
          );
        } else {
          Alert.alert(
            "Unable to Locate Delivery Address",
            "Could not determine customer delivery location coordinates. Please ensure the delivery address is complete and valid."
          );
          return;
        }
      }

      // Validate required contact information
      const pickupPhone =
        order.pickup_contact?.phone ||
        vendorProfile.phone_number ||
        vendorProfile.phone ||
        order.delivery_quote.pickup_phone_number;
      const dropoffPhone =
        order.dropoff_contact?.phone ||
        order.delivery_quote.dropoff_phone_number;

      if (!pickupPhone) {
        Alert.alert(
          "Missing Pickup Phone",
          "Pickup contact phone number is required for delivery."
        );
        return;
      }

      if (!dropoffPhone) {
        Alert.alert(
          "Missing Dropoff Phone",
          "Dropoff contact phone number is required for delivery."
        );
        return;
      }

      const dropoffName = order.dropoff_contact?.name;
      if (!dropoffName) {
        Alert.alert(
          "Missing Dropoff Contact",
          "Customer name is required for delivery."
        );
        return;
      }

      // Normalize addresses to ensure street_address is a string (not array)
      const normalizedPickupAddress = {
        ...pickupAddress,
        street_address: Array.isArray(pickupAddress.street_address)
          ? pickupAddress.street_address[0]
          : pickupAddress.street_address,
      };

      const normalizedDropoffAddress = {
        ...dropoffAddress,
        street_address: Array.isArray(dropoffAddress.street_address)
          ? dropoffAddress.street_address[0]
          : dropoffAddress.street_address,
      };

      // Prepare delivery payload for Uber API
      const deliveryPayload: any = {
        quote_id: order.delivery_quote.quote_id,
        pickup_address: normalizedPickupAddress,
        dropoff_address: normalizedDropoffAddress,
        pickup_name: order.pickup_contact?.name || vendorProfile.store_name,
        pickup_phone_number: toE164US(pickupPhone),
        dropoff_name: dropoffName,
        dropoff_phone_number: toE164US(dropoffPhone),
        manifest_items: manifestItems,
        pickup_ready_dt: DeliveryHelpers.createPickupReady(isTestMode),
        pickup_deadline_dt: DeliveryHelpers.createPickupDeadline(isTestMode),
        dropoff_ready_dt: DeliveryHelpers.createDropoffReady(isTestMode),
        dropoff_deadline_dt: DeliveryHelpers.createDropoffDeadline(isTestMode),
        // Add coordinates for more accurate delivery creation
        pickup_latitude: pickupLatitude,
        pickup_longitude: pickupLongitude,
        dropoff_latitude: dropoffLatitude,
        dropoff_longitude: dropoffLongitude,
        // Photo verification at pickup
        pickup_verification: { picture: true },
        // Leave at door (automatically enables photo verification at dropoff)
        deliverable_action: 'deliverable_action_leave_at_door',
      };

      // Add test specifications (robo courier) if test mode is enabled
      // Per Uber Direct API guide: https://developer.uber.com/docs/deliveries/guides/robocourier
      if (isTestMode) {
        const roboCourierSpec = DeliveryHelpers.createRoboCourierSpec('auto');
        deliveryPayload.test_specifications = roboCourierSpec.test_specifications;
      }

      console.log(
        `🚀 [VENDOR-DELIVERY] Creating ${
          isTestMode ? "TEST" : "REAL"
        } delivery with payload:`,
        deliveryPayload
      );

      // Helper function to get a fresh quote
      const getFreshQuote = async (): Promise<{ quote_id: string; fee?: number } | null> => {
        console.log("🔄 [VENDOR-DELIVERY] Getting fresh quote...");
        
        const quotePayload: any = {
          pickup_address: normalizedPickupAddress,
          dropoff_address: normalizedDropoffAddress,
          pickup_latitude: pickupLatitude,
          pickup_longitude: pickupLongitude,
          dropoff_latitude: dropoffLatitude,
          dropoff_longitude: dropoffLongitude,
        };
        
        // Add test specifications if in test mode
        if (isTestMode) {
          quotePayload.test_specifications = DeliveryHelpers.createRoboCourierSpec('auto').test_specifications;
        }
        
        try {
          const quoteResult = await supabase.functions.invoke("uber-quote", {
            body: quotePayload,
          });
          
          if (quoteResult.error || quoteResult.data?.error) {
            console.error("❌ [VENDOR-DELIVERY] Fresh quote failed:", quoteResult.error || quoteResult.data);
            return null;
          }
          
          const freshQuoteId = quoteResult.data?.id || quoteResult.data?.quote_id;
          // Extract fee from quote response (Uber returns fee in cents, convert to dollars)
          const freshFee = quoteResult.data?.fee ? Number(quoteResult.data.fee) / 100 : undefined;
          
          if (!freshQuoteId) {
            console.error("❌ [VENDOR-DELIVERY] No quote_id in fresh quote response");
            return null;
          }
          
          console.log("✅ [VENDOR-DELIVERY] Fresh quote obtained:", freshQuoteId, "Fee:", freshFee);
          return { quote_id: freshQuoteId, fee: freshFee };
        } catch (e) {
          console.error("❌ [VENDOR-DELIVERY] Error getting fresh quote:", e);
          return null;
        }
      };

      // Helper function to store quote variance for reconciliation
      const storeQuoteVariance = async (
        originalQuoteId: string,
        newQuoteId: string,
        originalFee: number,
        newFee: number,
        reason: string = 'expired_quote'
      ) => {
        const variance = newFee - originalFee;
        const variancePercentage = originalFee > 0 ? ((variance / originalFee) * 100) : 0;
        
        console.log(`📊 [QUOTE-VARIANCE] Recording variance: Original fee: $${originalFee.toFixed(2)}, New fee: $${newFee.toFixed(2)}, Variance: $${variance.toFixed(2)} (${variancePercentage.toFixed(2)}%)`);
        
        try {
          const { error: varianceError } = await supabase
            .from("quote_variances")
            .insert({
              vendor_order_id: order.id,
              parent_order_id: order.parent_order_id,
              original_quote_id: originalQuoteId,
              new_quote_id: newQuoteId,
              original_fee: originalFee,
              new_fee: newFee,
              variance: variance,
              variance_percentage: variancePercentage,
              reason: reason,
            });
          
          if (varianceError) {
            console.error("❌ [QUOTE-VARIANCE] Failed to store variance:", varianceError);
          } else {
            console.log("✅ [QUOTE-VARIANCE] Variance recorded successfully for reconciliation");
          }
        } catch (e) {
          console.error("❌ [QUOTE-VARIANCE] Error storing variance:", e);
          // Don't fail the delivery for this - variance storage is for reconciliation only
        }
      };

      // Helper function to create delivery with a given quote
      const createDeliveryWithQuote = async (quoteId: string) => {
        const payload = {
          ...deliveryPayload,
          quote_id: quoteId,
          vendor_order_id: order.id,
          parent_order_id: order.parent_order_id,
        };
        
        return await supabase.functions.invoke("uber-create-delivery", { body: payload });
      };

      // Helper to check if delivery result is a quote-related failure
      const isQuoteFailure = (result: any): boolean => {
        if (!result) return false;
        
        const errorCode = result.data?.details?.code || result.data?.error || result.data?.code;
        const errorMessage = (result.data?.message || result.error?.message || "").toLowerCase();
        
        // Check for various quote-related failures
        return (
          errorCode === "expired_quote" ||
          errorCode === "invalid_quote" ||
          errorCode === "quote_not_found" ||
          errorMessage.includes("expired") ||
          errorMessage.includes("quote") ||
          errorMessage.includes("invalid_parameters") // Often quote-related
        );
      };

      // Helper to attempt delivery with fresh quote retry on failure
      const attemptDeliveryWithRetry = async (
        initialQuoteId: string | null,
        originalFee: number
      ): Promise<{ success: boolean; result?: any; error?: string }> => {
        let deliveryResult;
        let usedFreshQuote = false;
        let freshQuoteData: { quote_id: string; fee?: number } | null = null;
        
        // First attempt with existing quote (if available)
        if (initialQuoteId) {
          console.log("✅ [DELIVERY] Trying existing quote:", initialQuoteId);
          
          try {
            deliveryResult = await createDeliveryWithQuote(initialQuoteId);
          } catch (error) {
            console.error("[DELIVERY] Delivery creation threw exception:", error);
            deliveryResult = { error: { message: error instanceof Error ? error.message : "Unknown error" } };
          }
          
          // Check if the delivery succeeded
          const hasError = deliveryResult.error || deliveryResult.data?.error || typeof deliveryResult.data === "string";
          
          if (hasError) {
            const isQuoteRelated = isQuoteFailure(deliveryResult);
            console.log(`⚠️ [DELIVERY] First attempt failed. Quote-related: ${isQuoteRelated}`);
            
            // If it's a quote-related failure, try getting a fresh quote
            if (isQuoteRelated) {
              console.log("🔄 [DELIVERY] Attempting to get fresh quote and retry...");
              
              freshQuoteData = await getFreshQuote();
              if (!freshQuoteData) {
                return { 
                  success: false, 
                  error: "Original quote failed and unable to get a new quote. Please try again." 
                };
              }
              
              // Store variance if fees differ (for monthly reconciliation)
              if (freshQuoteData.fee !== undefined && originalFee > 0 && freshQuoteData.fee !== originalFee) {
                const reason = isQuoteFailure(deliveryResult) ? 
                  (deliveryResult.data?.details?.code || 'quote_failure') : 
                  'quote_failure';
                  
                await storeQuoteVariance(
                  initialQuoteId,
                  freshQuoteData.quote_id,
                  originalFee,
                  freshQuoteData.fee,
                  reason
                );
              }
              
              // Update the order's delivery_quote with the fresh quote
              await supabase
                .from("vendor_orders")
                .update({
                  delivery_quote: {
                    ...order.delivery_quote,
                    quote_id: freshQuoteData.quote_id,
                    fee: freshQuoteData.fee ?? order.delivery_quote?.fee,
                  },
                })
                .eq("id", order.id);
              
              // Retry with fresh quote
              console.log("🔄 [DELIVERY] Retrying with fresh quote:", freshQuoteData.quote_id);
              try {
                deliveryResult = await createDeliveryWithQuote(freshQuoteData.quote_id);
                usedFreshQuote = true;
              } catch (retryError) {
                console.error("[DELIVERY] Retry with fresh quote threw exception:", retryError);
                deliveryResult = { error: { message: retryError instanceof Error ? retryError.message : "Unknown error" } };
              }
            }
          }
        } else {
          // No existing quote - get a fresh one
          console.log("⚠️ [VENDOR-DELIVERY] No existing quote, getting fresh quote...");
          freshQuoteData = await getFreshQuote();
          if (!freshQuoteData) {
            return { success: false, error: "Failed to get delivery quote. Please try again." };
          }
          
          deliveryResult = await createDeliveryWithQuote(freshQuoteData.quote_id);
          usedFreshQuote = true;
        }
        
        // Final error check
        if (deliveryResult.error) {
          return { 
            success: false, 
            error: deliveryResult.error.message || "Edge function error" 
          };
        }
        
        if (deliveryResult.data?.error || typeof deliveryResult.data === "string") {
          const errorMsg = deliveryResult.data?.message || deliveryResult.data?.error || deliveryResult.data || "Unknown error";
          return { success: false, error: errorMsg };
        }
        
        return { success: true, result: deliveryResult };
      };

      // Attempt delivery with automatic retry on quote failure
      const existingQuoteId = order.delivery_quote?.quote_id;
      // Original fee is stored in cents, convert to dollars for comparison
      const originalFee = order.delivery_quote?.fee ? Number(order.delivery_quote.fee) / 100 : 0;
      
      const deliveryAttempt = await attemptDeliveryWithRetry(
        existingQuoteId || null,
        originalFee
      );
      
      if (!deliveryAttempt.success) {
        await handleDeliveryProviderError(order, deliveryAttempt.error || "Delivery creation failed");
        return;
      }
      
      const deliveryResult = deliveryAttempt.result;

      const deliveryData = deliveryResult.data;
      const actualDeliveryId =
        deliveryData?.id ||
        deliveryData?.delivery_id ||
        deliveryData?.delivery?.id;
      const actualTrackingUrl =
        deliveryData?.tracking_url || deliveryData?.tracking?.url;

      if (!actualDeliveryId) {
        console.error(
          "❌ [VENDOR-DELIVERY] No delivery ID in response:",
          deliveryData
        );
        await handleDeliveryProviderError(order, "Uber API did not return a valid delivery ID");
        return;
      }

      console.log("✅ [VENDOR-DELIVERY] Delivery created successfully:", {
        deliveryId: actualDeliveryId,
        trackingUrl: actualTrackingUrl,
      });

      // Update vendor order with delivery information and status
      const { error: updateError } = await supabase
        .from("vendor_orders")
        .update({
          delivery_id: actualDeliveryId,
          tracking_url: actualTrackingUrl,
          status: "pickup_pending",
          updated_at: new Date().toISOString(),
        })
        .eq("id", order.id);

      if (updateError) throw updateError;

      // Also update the main orders table if parent_order_id exists
      if (order.parent_order_id) {
        const { error: orderError } = await supabase
          .from("orders")
          .update({
            status: "pickup_pending",
            delivery_id: actualDeliveryId,
            tracking_url: actualTrackingUrl,
            updated_at: new Date().toISOString(),
          })
          .eq("id", order.parent_order_id);
        if (orderError) {
          console.warn("Failed to update orders table:", orderError);
          // Don't throw here - vendor_orders update succeeded
        }
      }

      // Update local state
      setOrder((o) =>
        o
          ? {
              ...o,
              delivery_id: actualDeliveryId,
              tracking_url: actualTrackingUrl,
              status: "pickup_pending",
            }
          : o
      );

      Alert.alert(
        isTestMode ? "Test Delivery Started" : "Delivery Started",
        isTestMode
          ? "Test delivery simulation started! Status will update automatically every ~30 seconds."
          : "Courier has been dispatched and is on the way to pickup."
      );
    } catch (e: any) {
      console.error("❌ [VENDOR-DELIVERY] Error:", e);
      Alert.alert("Error", e?.message || "Failed to start delivery");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !order) {
    return (
      <View style={styles.center}>
        <ActivityIndicator color={colors.primary} />
        <Text style={styles.muted}>Loading order…</Text>
      </View>
    );
  }

  const money = (n?: number | null) =>
    typeof n === "number" ? formatPrice(n) : "—";

  return (
    <ScrollView
      style={styles.container}
      contentInsetAdjustmentBehavior="automatic"
    >
      {/* Header */}
      <View style={styles.headerCard}>
        <View style={styles.headerRow}>
          <Text style={styles.title}>Order #{order.id.slice(0, 8)}</Text>
          <View
            style={[styles.chip, { backgroundColor: statusMeta.bgColor }]}
          >
            <Feather
              name={statusMeta.icon as any}
              size={16}
              color="#000000"
            />
            <Text style={[styles.chipText, { color: "#000000" }]}>
              {statusMeta.label}
            </Text>
          </View>
        </View>
        <Text style={styles.muted}>
          Created {format(new Date(order.created_at), "MMM d, yyyy, h:mm a")}
        </Text>
      </View>

      {/* Items */}
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Items</Text>
        {order.items.map((it, idx) => (
          <View key={`${order.id}-item-${idx}`} style={styles.itemRow}>
            <View style={styles.thumbWrap}>
              {it.image ? (
                <Image source={{ uri: it.image }} style={styles.thumb} contentFit="cover" />
              ) : (
                <View style={styles.thumbPlaceholder}>
                  <Feather name="image" size={18} color="#9CA3AF" />
                </View>
              )}
            </View>

            <View style={{ flex: 1 }}>
              <Text style={styles.itemName} numberOfLines={2}>
                {it.name}
              </Text>
              <Text style={styles.itemMeta}>
                Qty {it.quantity}
                {typeof it.unit_price === "number"
                  ? ` · ${money(it.unit_price)}`
                  : ""}
              </Text>
            </View>
          </View>
        ))}
      </View>

      {/* Totals */}
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Summary</Text>
        <View style={styles.rowBetween}>
          <Text style={styles.muted}>Items Total</Text>
          <Text style={styles.value}>{money(order.sub_total)}</Text>
        </View>
        {typeof order.sub_total === "number" && (
          <View style={styles.rowBetween}>
            <Text style={styles.muted}>Processing Fee</Text>
            <Text style={styles.value}>
              -{money(DeliveryHelpers.calculateSellerFee(order.sub_total))}
            </Text>
          </View>
        )}
        <View style={[styles.rowBetween, { marginTop: 8 }]}>
          <Text style={styles.totalLabel}>You'll Receive</Text>
          <Text style={styles.totalValue}>
            {money(
              (order.sub_total ?? 0) -
              DeliveryHelpers.calculateSellerFee(order.sub_total ?? 0)
            )}
          </Text>
        </View>
        <TouchableOpacity
          style={styles.paymentInfoLink}
          onPress={() => navigation.navigate("PaymentProcessingInfo" as never)}
        >
          <Feather name="info" size={14} color={colors.primary} />
          <Text style={styles.paymentInfoLinkText}>
            When will I receive payment?
          </Text>
          <Feather name="chevron-right" size={14} color={colors.primary} />
        </TouchableOpacity>
      </View>

      {/* Actions */}
      <View style={styles.card}>
        <Text style={styles.sectionTitle}>Actions</Text>

        {/* Message Buyer button */}
        {order?.buyer_id && (
          <TouchableOpacity
            style={[styles.btn, styles.btnSecondary, startingChat && styles.disabled]}
            disabled={startingChat}
            onPress={startChatWithBuyer}
          >
            {startingChat ? (
              <ActivityIndicator size="small" color="#000" />
            ) : (
              <Feather name="message-circle" size={18} color="#000" />
            )}
            <Text style={styles.btnSecondaryText}>Message Buyer</Text>
          </TouchableOpacity>
        )}

        {canApprove && (
          <TouchableOpacity
            style={[styles.btn, styles.btnPrimary, saving && styles.disabled]}
            disabled={saving}
            onPress={approve}
          >
            <Feather name="check" size={18} color="#000" />
            <Text style={styles.btnPrimaryText}>Confirm Order</Text>
          </TouchableOpacity>
        )}

        {canReject && (
          <TouchableOpacity
            style={[styles.btn, styles.btnDanger, saving && styles.disabled]}
            disabled={saving}
            onPress={reject}
          >
            <Feather name="x" size={18} color="#000" />
            <Text style={styles.btnDangerText}>Reject Order</Text>
          </TouchableOpacity>
        )}

        {canSetWrappingUp && (
          <TouchableOpacity
            style={[styles.btn, styles.btnPrimary, saving && styles.disabled]}
            disabled={saving}
            onPress={setWrappingUp}
          >
            <Feather name="package" size={18} color="#000" />
            <Text style={styles.btnPrimaryText}>Set "Wrapping Up"</Text>
          </TouchableOpacity>
        )}

        {canPack && (
          <TouchableOpacity
            style={[styles.btn, styles.btnPacked, saving && styles.disabled]}
            disabled={saving}
            onPress={markPacked}
          >
            <Feather name="archive" size={18} color="#000" />
            <Text style={styles.btnPackedText}>Mark Packed</Text>
          </TouchableOpacity>
        )}

        {canRequestDelivery && (
          <TouchableOpacity
            style={[styles.btn, styles.btnPrimary, saving && styles.disabled]}
            disabled={saving}
            onPress={requestDelivery}
          >
            <Feather name="truck" size={18} color="#000" />
            <Text style={styles.btnPrimaryText}>Start Delivery</Text>
          </TouchableOpacity>
        )}

        {ENABLE_CANCEL_DELIVERY && canCancelDelivery && (
          <TouchableOpacity
            style={[
              styles.btn,
              styles.btnDanger,
              saving && styles.disabled,
            ]}
            disabled={saving}
            onPress={cancelDelivery}
          >
            <Feather name="truck" size={18} color="#000" />
            <Text style={styles.btnDangerText}>Cancel Delivery</Text>
          </TouchableOpacity>
        )}

        {ENABLE_CANCEL_DELIVERY && canCancel && (
          <TouchableOpacity
            style={[
              styles.btn,
              styles.btnReject,
              saving && styles.disabled,
            ]}
            disabled={saving}
            onPress={cancelOrder}
          >
            <Feather name="x" size={18} color={"#000"} />
            <Text style={styles.btnRejectText}>Cancel</Text>
          </TouchableOpacity>
        )}
      </View>

      <View style={{ height: 24 }} />
    </ScrollView>
  );
}

const createStyles = (colors: ThemeColors) =>
  StyleSheet.create({
    container: { flex: 1, backgroundColor: colors.background },

    center: { flex: 1, alignItems: "center", justifyContent: "center", gap: 8 },
    muted: { color: colors.textLight },

  headerCard: {
    backgroundColor: colors.surface,
    marginHorizontal: spacing.lg,
    marginTop: spacing.md,
    padding: spacing.md,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.03,
    shadowRadius: 8,
    elevation: 2,
    gap: 4,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
  },
  title: { fontSize: 18, fontWeight: "800", color: colors.text },

  chip: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
  },
  chipText: { fontSize: 12, fontWeight: "700" },

  card: {
    backgroundColor: colors.surface,
    marginHorizontal: spacing.lg,
    marginTop: spacing.md,
    padding: spacing.md,
    borderRadius: 20,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.03,
    shadowRadius: 8,
    elevation: 2,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "800",
    color: colors.text,
    marginBottom: spacing.md,
  },

  itemRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: spacing.md,
    marginVertical: 8,
  },
  thumbWrap: { width: 60 },
  thumb: {
    width: 60,
    height: 60,
    borderRadius: 12,
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: colors.border,
  },
  thumbPlaceholder: {
    width: 60,
    height: 60,
    borderRadius: 12,
    backgroundColor: colors.surface,
    alignItems: "center",
    justifyContent: "center",
    borderWidth: 1,
    borderColor: colors.border,
  },
  itemName: { fontSize: 15, fontWeight: "700", color: colors.text },
  itemMeta: { fontSize: 13, color: colors.textLight, marginTop: 2 },

  rowBetween: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginVertical: 4,
  },
  value: { color: colors.text, fontWeight: "600", fontSize: 14 },
  totalLabel: { fontSize: 16, color: colors.text, fontWeight: "800" },
  totalValue: { fontSize: 18, color: colors.text, fontWeight: "800" },

  feeNote: {
    fontSize: 12,
    color: colors.textLight,
    fontStyle: "italic",
  },
  paymentInfoLink: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: spacing.sm,
    marginTop: spacing.sm,
    backgroundColor: colors.primary + '10',
    borderRadius: 12,
    paddingHorizontal: spacing.sm,
  },
  paymentInfoLinkText: {
    color: colors.primary,
    fontSize: 13,
    fontWeight: "600",
    marginLeft: spacing.xs,
    marginRight: spacing.xs,
    flex: 1,
  },

  btn: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    height: 52,
    borderRadius: 26,
    marginTop: 12,
  },
  btnPrimary: { backgroundColor: "#00A86B" }, // jade green
  btnPrimaryText: { color: "#000", fontWeight: "700", fontSize: 15 },

  btnSecondary: {
    backgroundColor: "#00A86B", // jade green
  },
  btnSecondaryText: { color: "#000", fontWeight: "700", fontSize: 15 },

  btnPacked: { backgroundColor: "#00A86B" }, // jade green (same as primary)
  btnPackedText: { color: "#000", fontWeight: "700", fontSize: 15 },

  btnDanger: { backgroundColor: "#F43F5E" }, // rose red
  btnDangerText: { color: "#000", fontWeight: "700", fontSize: 15 },

  disabled: { opacity: 0.6 },

  req: {}, // (kept for parity with your other screens)
  trackingText: {
    ...typography.body,
    color: colors.primary,
    fontWeight: "700",
  },
  btnDangerOutline: {
    backgroundColor: colors.surface,
    borderWidth: 1,
    borderColor: "#F43F5E",
  },
  btnDangerOutlineText: {
    color: "#F43F5E",
    fontWeight: "800",
  },
  btnReject: {
    backgroundColor: "#F43F5E", // rose red
  },
  btnRejectText: {
    color: "#000",
    fontWeight: "700",
    fontSize: 15,
  },

    testModeContainer: {
      backgroundColor: colors.backgroundSecondary,
      marginTop: spacing.sm,
      padding: spacing.md,
      borderRadius: 12,
      borderWidth: 1,
      borderColor: colors.border,
    },
    testModeToggle: {
      flexDirection: "row",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: spacing.xs,
    },
    testModeLabel: {
      fontSize: 16,
      fontWeight: "600",
      color: colors.text,
    },
    testModeNote: {
      fontSize: 13,
      color: colors.primary,
      fontStyle: "italic",
      textAlign: "center",
      marginTop: spacing.xs,
    },
  });
